import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, MapPin, Zap, Leaf } from "lucide-react";
import { COMPANY, IMAGES } from "../lib/constants";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";

export default function ChiSiamo() {
  return (
    <div data-testid="page-chi-siamo">
      <SEO
        title="Chi Siamo — Oltre 20 Anni di Esperienza"
        description="Impresa Edile Lici S.a.s. di Lici Armand e C. Da oltre 20 anni installiamo e manutenziamo impianti fotovoltaici in Lombardia. Affidabilità, competenza e sicurezza certificata."
        path="/chi-siamo"
        image={IMAGES.villaWithPv}
      />
      {/* HERO */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <img
          src={IMAGES.villaWithPv}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-16 w-full">
          <div className="flex items-center gap-3 mb-4">
            <span className="accent-line" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#FED7AA]">Chi Siamo</span>
          </div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[1.05] max-w-3xl">
            Due decenni di passione per l'<span className="italic text-[#FED7AA]">energia pulita</span>.
          </h1>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="La nostra storia"
              title="Oltre 20 anni di eccellenza nel fotovoltaico"
              description="Da più di due decenni siamo il punto di riferimento per l'efficienza energetica nel territorio. La nostra storia è fatta di centinaia di impianti realizzati, di clienti diventati amici e di un costante aggiornamento sulle tecnologie più innovative del settore."
            />
            <div className="mt-8 space-y-5 text-[#111827] leading-relaxed">
              <p>
                Affidabilità, competenza e aggiornamento continuo sono i pilastri che ci hanno
                permesso di crescere insieme ai nostri clienti. Scegliere Impresa Edile Lici significa
                avere un partner solido, in grado di gestire l'intero ciclo di vita del vostro
                impianto: dalla progettazione iniziale, alla scelta dei materiali, all'installazione,
                fino alla manutenzione programmata e al lavaggio professionale.
              </p>
              <p>
                Operiamo su tutto il territorio lombardo, con base operativa a Seveso, servendo
                clienti privati con installazioni residenziali di pregio e aziende con grandi impianti
                industriali su tetto e a terra.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#F3F4F6] border border-[#E5E7EB] rounded-2xl p-8 grain">
              <h3 className="font-serif text-2xl text-[#0F5132] mb-6">Numeri che parlano</h3>
              <div className="space-y-6">
                {[
                  { v: "20+", l: "Anni di attività" },
                  { v: "500+", l: "Impianti realizzati" },
                  { v: "25%", l: "Resa media recuperata col lavaggio" },
                  { v: "100%", l: "Personale formato per lavori in quota" },
                ].map((s) => (
                  <div key={s.l} className="flex items-baseline justify-between border-b border-[#E5E7EB] pb-4 last:border-b-0">
                    <span className="font-serif text-3xl text-[#0F5132]">{s.v}</span>
                    <span className="text-sm text-[#4B5563] text-right max-w-[60%]">{s.l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALORI */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <SectionHeading
            eyebrow="I nostri valori"
            title="Ciò in cui crediamo"
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                Icon: Award,
                title: "Qualità senza compromessi",
                text: "Solo materiali certificati e di prima scelta. Inverter, pannelli, strutture: ogni componente è selezionato per durare nel tempo.",
              },
              {
                Icon: Users,
                title: "Relazione con il cliente",
                text: "Ascoltiamo, consigliamo, accompagniamo. Il preventivo è solo l'inizio di un rapporto che dura per tutta la vita dell'impianto.",
              },
              {
                Icon: Zap,
                title: "Efficienza misurabile",
                text: "Ogni intervento è documentato con report tecnici e fotografici. Sappiamo esattamente quanto stai producendo e perché.",
              },
              {
                Icon: Leaf,
                title: "Impegno ambientale",
                text: "Il fotovoltaico è la nostra missione: contribuire alla transizione energetica un impianto alla volta.",
              },
              {
                Icon: MapPin,
                title: "Radicati sul territorio",
                text: "Sede a Seveso (MB), interventi in tutta la Lombardia. Vicini quando serve, rapidi negli interventi di assistenza.",
              },
              {
                Icon: Award,
                title: "Personale certificato",
                text: "Formazione continua su sicurezza, lavori in quota e nuove tecnologie. La sicurezza non è negoziabile.",
              },
            ].map((v) => (
              <div
                key={v.title}
                className="bg-white border border-[#E5E7EB] rounded-2xl p-7 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
              >
                <div className="h-12 w-12 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center mb-5">
                  <v.Icon className="h-6 w-6" />
                </div>
                <h3 className="font-serif text-xl text-[#0F5132] mb-3">{v.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#0F5132]">
            Vuoi conoscerci meglio?
          </h2>
          <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">
            Contattaci per fissare un sopralluogo gratuito o per una consulenza tecnica senza impegno.
          </p>
          <Link
            to="/contatti"
            data-testid="chi-siamo-cta"
            className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0F5132] text-white font-medium hover:bg-[#0A3622] transition-all"
          >
            Contattaci ora <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
